import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaCheck } from 'react-icons/fa';
import { motion } from 'framer-motion';
import Classes from './ContactUs.module.css';
import { fadeUpVariant } from '../../../utils/motionVariants';
import Seo from '../../GlobalComp/Seo/Seo';
import { SITE } from '../../../data/siteConfig';
import { formatPhone, isValidPhone, prefetchToken, sendContact } from '../../../utils/contactForm';

const EMPTY = { firstName: '', lastName: '', email: '', phoneNumber: '', subject: '', message: '', company: '' };

export default function ContactUs() {
    const [formData, setFormData] = useState(EMPTY);
    const [status, setStatus] = useState('');
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);

    useEffect(() => { prefetchToken(); }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: name === 'phoneNumber' ? formatPhone(value) : value });
    };

    // Sent by api/contact.js, same as the hero estimate form.
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!isValidPhone(formData.phoneNumber)) {
            setStatus('Please enter a valid 10-digit phone number.');
            return;
        }
        setSending(true);
        setStatus('');
        try {
            await sendContact({
                name: `${formData.firstName} ${formData.lastName}`.trim(),
                email: formData.email,
                phone: formData.phoneNumber,
                message: formData.subject ? `Subject: ${formData.subject}\n\n${formData.message}` : formData.message,
                company: formData.company,
            });
            setFormData(EMPTY);
            setSent(true);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (error) {
            setStatus(`Error sending message${error.message ? ` (${error.message})` : ''}. Please try again or call ${SITE.phone}.`);
        } finally {
            setSending(false);
        }
    };

    if (sent) {
        return (
            <main className={Classes.page}>
                <Seo title="Message Sent | 561-FIX-HOME" description="Thanks for contacting 561-FIX-HOME." path="/contact-us" noindex />
                <div className="container">
                    <motion.div {...fadeUpVariant(0.05)} className={Classes.sentCard} role="status">
                        <span className={Classes.check} aria-hidden="true"><FaCheck /></span>
                        <h1 className={Classes.sentTitle}>Message sent!</h1>
                        <p className={Classes.sentText}>
                            Thanks for reaching out. A member of our team will get back to you within 24–48 hours.
                            Need help sooner? Call us at <a href={`tel:${SITE.phoneE164}`}>{SITE.phone}</a>.
                        </p>
                        <Link to="/" className="btn btn-dark">Back to Homepage</Link>
                    </motion.div>
                </div>
            </main>
        );
    }

    return (
        <main className={Classes.page}>
            <Seo
                title="Contact Us | 561-FIX-HOME | Boca Raton General Contractor"
                description={`Contact 561-FIX-HOME for a free estimate. Call ${SITE.phone}, book online, or send us a message. Serving Boca Raton and South Florida.`}
                path="/contact-us"
            />

            <div className={`container section-head center ${Classes.head}`}>
                <div className="eyebrow">Contact Us</div>
                <motion.h1 {...fadeUpVariant(0.1)} className="section-title">Let’s Talk About Your Project</motion.h1>
                <p className="section-lead">Call, book online, or send us a message. We’ll get back to you quickly with a quote.</p>
            </div>

            <div className={`container ${Classes.grid}`}>

                <motion.div {...fadeUpVariant(0.2)} className={Classes.formContainer}>
                    <h2>Send us a message</h2>
                    <form onSubmit={handleSubmit}>
                        <div className={Classes.twoElem}>
                            <input type="text" placeholder="First name" name="firstName" autoComplete="given-name" value={formData.firstName} onChange={handleChange} required />
                            <input type="text" placeholder="Last name" name="lastName" autoComplete="family-name" value={formData.lastName} onChange={handleChange} required />
                        </div>
                        <div className={Classes.twoElem}>
                            <input type="email" placeholder="Email" name="email" autoComplete="email" value={formData.email} onChange={handleChange} required />
                            <input type="tel" placeholder="Phone number" name="phoneNumber" autoComplete="tel" inputMode="tel" value={formData.phoneNumber} onChange={handleChange} required />
                        </div>
                        <input type="text" placeholder="Subject" name="subject" value={formData.subject} onChange={handleChange} />
                        <textarea placeholder="How can we help?" name="message" value={formData.message} onChange={handleChange} rows={6} required />
                        {/* Hidden from people; bots that fill it in are ignored by api/contact.js */}
                        <input name="company" value={formData.company} onChange={handleChange} tabIndex={-1} autoComplete="off" aria-hidden="true" className={Classes.honeypot} />
                        <button type="submit" className="btn btn-dark" disabled={sending}>{sending ? 'Sending…' : 'Send Message'}</button>
                        {status && <p className={Classes.status}>{status}</p>}
                    </form>
                </motion.div>
            </div>
        </main>
    );
}
